/**
 * Service Requests Page
 * Gestión de solicitudes de servicio enviadas por los clientes
 */

import { useState } from "react";
import { useServiceFlow } from "@/contexts";
import { Icon } from "@/components/ui";
import styles from "./ServiceRequestsPage.module.css";

type RequestStatus =
  | "pending"
  | "reviewing"
  | "approved"
  | "scheduled"
  | "rejected";
type RequestUrgency = "low" | "medium" | "high";

interface ServiceRequest {
  id: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  type: string;
  description: string;
  urgency: RequestUrgency;
  preferredDate: string;
  status: RequestStatus;
  createdAt: Date;
  notes?: string;
}

const MOCK_REQUESTS: ServiceRequest[] = [
  {
    id: "REQ-2026-001",
    clientId: "hotel-meridian",
    clientName: "Hotel Meridian",
    clientEmail: "cliente@hotelmeridian.com",
    clientPhone: "934567890",
    type: "Desratización",
    description:
      "Se han detectado signos de roedores en el sótano del hotel. Necesitamos inspección y tratamiento urgente.",
    urgency: "high",
    preferredDate: "2026-01-28",
    status: "pending",
    createdAt: new Date("2026-01-27T09:30:00"),
  },
  {
    id: "REQ-2026-002",
    clientId: "rest-buen-sabor",
    clientName: "Restaurante El Buen Sabor",
    clientEmail: "info@elbuensabor.com",
    clientPhone: "912345678",
    type: "Control de Cucarachas",
    description:
      "Mantenimiento preventivo mensual programado. Revisar cocina y almacén.",
    urgency: "medium",
    preferredDate: "2026-02-01",
    status: "reviewing",
    createdAt: new Date("2026-01-26T14:20:00"),
  },
  {
    id: "REQ-2026-003",
    clientId: "oficinas-techcorp",
    clientName: "Oficinas TechCorp",
    clientEmail: "facilities@techcorp.com",
    clientPhone: "923456789",
    type: "Control de Hormigas",
    description:
      "Aparición de hormigas en la zona de oficinas del tercer piso. Preferiblemente tratamiento fuera de horario laboral.",
    urgency: "low",
    preferredDate: "2026-02-05",
    status: "approved",
    createdAt: new Date("2026-01-25T11:45:00"),
  },
  {
    id: "REQ-2026-004",
    clientId: "colegio-san-juan",
    clientName: "Colegio San Juan",
    clientEmail: "admin@colegiosanjuan.edu",
    clientPhone: "934123456",
    type: "Inspección General",
    description:
      "Inspección trimestral de instalaciones escolares según normativa sanitaria.",
    urgency: "medium",
    preferredDate: "2026-01-30",
    status: "scheduled",
    createdAt: new Date("2026-01-24T16:10:00"),
  },
];

export const ServiceRequestsPage = () => {
  const { createQuoteFromRequest, setPendingQuote } = useServiceFlow();
  const [requests, setRequests] = useState<ServiceRequest[]>(MOCK_REQUESTS);
  const [filterStatus, setFilterStatus] = useState<RequestStatus | "all">(
    "all",
  );
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(
    null,
  );

  const filteredRequests =
    filterStatus === "all"
      ? requests
      : requests.filter((r) => r.status === filterStatus);

  const getStatusInfo = (status: RequestStatus) => {
    const statusMap = {
      pending: { label: "Pendiente", color: "#f59e0b", bgColor: "#fef3c7" },
      reviewing: { label: "En Revisión", color: "#3b82f6", bgColor: "#dbeafe" },
      approved: { label: "Aprobada", color: "#10b981", bgColor: "#d1fae5" },
      scheduled: { label: "Programada", color: "#8b5cf6", bgColor: "#ede9fe" },
      rejected: { label: "Rechazada", color: "#ef4444", bgColor: "#fee2e2" },
    };
    return statusMap[status];
  };

  const getUrgencyInfo = (urgency: RequestUrgency) => {
    const urgencyMap = {
      low: { label: "Baja", color: "#6b7280" },
      medium: { label: "Media", color: "#f59e0b" },
      high: { label: "Alta", color: "#ef4444" },
    };
    return urgencyMap[urgency];
  };

  const formatDate = (date: Date | string): string => {
    const d = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(d);
  };

  const formatDateTime = (date: Date): string => {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const handleStatusChange = (requestId: string, newStatus: RequestStatus) => {
    setRequests((prev) =>
      prev.map((req) =>
        req.id === requestId ? { ...req, status: newStatus } : req,
      ),
    );
    if (selectedRequest?.id === requestId) {
      setSelectedRequest({ ...selectedRequest, status: newStatus });
    }
  };

  const handleApprove = (requestId: string, clientName: string) => {
    const request = requests.find((r) => r.id === requestId);
    if (!request) return;

    // Mark as approved
    handleStatusChange(requestId, "approved");

    // Create quote from request
    const quote = createQuoteFromRequest(request);
    setPendingQuote(quote);

    // Navigate to quotes page
    setTimeout(() => {
      window.location.hash = "#/quotes";
    }, 300);
  };

  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const reviewingCount = requests.filter(
    (r) => r.status === "reviewing",
  ).length;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>📋 Solicitudes de Servicio</h1>
          <p className={styles.subtitle}>
            Gestión de peticiones de clientes desde el portal web
          </p>
        </div>

        <div className={styles.stats}>
          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
            >
              <Icon name="clock" size={24} />
            </div>
            <div className={styles.statContent}>
              <span className={styles.statLabel}>Pendientes</span>
              <span className={styles.statValue}>{pendingCount}</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: "#dbeafe", color: "#1e40af" }}
            >
              <Icon name="document" size={24} />
            </div>
            <div className={styles.statContent}>
              <span className={styles.statLabel}>En Revisión</span>
              <span className={styles.statValue}>{reviewingCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <button
          className={`${styles.filterButton} ${filterStatus === "all" ? styles.active : ""}`}
          onClick={() => setFilterStatus("all")}
        >
          Todas ({requests.length})
        </button>
        <button
          className={`${styles.filterButton} ${filterStatus === "pending" ? styles.active : ""}`}
          onClick={() => setFilterStatus("pending")}
        >
          Pendientes ({pendingCount})
        </button>
        <button
          className={`${styles.filterButton} ${filterStatus === "reviewing" ? styles.active : ""}`}
          onClick={() => setFilterStatus("reviewing")}
        >
          En Revisión ({reviewingCount})
        </button>
        <button
          className={`${styles.filterButton} ${filterStatus === "approved" ? styles.active : ""}`}
          onClick={() => setFilterStatus("approved")}
        >
          Aprobadas
        </button>
        <button
          className={`${styles.filterButton} ${filterStatus === "scheduled" ? styles.active : ""}`}
          onClick={() => setFilterStatus("scheduled")}
        >
          Programadas
        </button>
      </div>

      {/* Requests Grid */}
      <div className={styles.grid}>
        {filteredRequests.map((request) => {
          const statusInfo = getStatusInfo(request.status);
          const urgencyInfo = getUrgencyInfo(request.urgency);

          return (
            <div
              key={request.id}
              className={styles.requestCard}
              onClick={() => setSelectedRequest(request)}
            >
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <span className={styles.requestId}>{request.id}</span>
                  <span
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: statusInfo.bgColor,
                      color: statusInfo.color,
                    }}
                  >
                    {statusInfo.label}
                  </span>
                </div>
                <span
                  className={styles.urgencyBadge}
                  style={{ color: urgencyInfo.color }}
                >
                  <Icon name="alertTriangle" size={14} />
                  {urgencyInfo.label}
                </span>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.clientInfo}>
                  <Icon name="users" size={16} />
                  <strong>{request.clientName}</strong>
                </div>

                <div className={styles.serviceType}>
                  <Icon name="bug" size={16} />
                  {request.type}
                </div>

                <p className={styles.description}>{request.description}</p>

                <div className={styles.cardMeta}>
                  <div className={styles.metaItem}>
                    <Icon name="calendar" size={14} />
                    <span>
                      Fecha preferida: {formatDate(request.preferredDate)}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <Icon name="clock" size={14} />
                    <span>Recibida: {formatDateTime(request.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className={styles.cardActions}>
                {request.status === "pending" && (
                  <>
                    <button
                      className={`${styles.actionButton} ${styles.review}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(request.id, "reviewing");
                      }}
                    >
                      <Icon name="eye" size={16} />
                      Revisar
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.approve}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(request.id, request.clientName);
                      }}
                    >
                      <Icon name="document" size={16} />
                      Crear Presupuesto
                    </button>
                  </>
                )}
                {request.status === "reviewing" && (
                  <>
                    <button
                      className={`${styles.actionButton} ${styles.approve}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(request.id, request.clientName);
                      }}
                    >
                      <Icon name="document" size={16} />
                      Crear Presupuesto
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.reject}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(request.id, "rejected");
                      }}
                    >
                      <Icon name="xCircle" size={16} />
                      Rechazar
                    </button>
                  </>
                )}
                {request.status === "approved" && (
                  <button
                    className={`${styles.actionButton} ${styles.schedule}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.hash = "#/quotes";
                    }}
                  >
                    <Icon name="document" size={16} />
                    Ver en Presupuestos
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div className={styles.modal} onClick={() => setSelectedRequest(null)}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>Detalles de Solicitud</h2>
              <button
                className={styles.closeButton}
                onClick={() => setSelectedRequest(null)}
              >
                <Icon name="xCircle" size={24} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.detailSection}>
                <h3>Información de la Solicitud</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>ID:</span>
                    <span className={styles.detailValue}>
                      {selectedRequest.id}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Estado:</span>
                    <span
                      className={styles.statusBadge}
                      style={{
                        backgroundColor: getStatusInfo(selectedRequest.status)
                          .bgColor,
                        color: getStatusInfo(selectedRequest.status).color,
                      }}
                    >
                      {getStatusInfo(selectedRequest.status).label}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Urgencia:</span>
                    <span
                      style={{
                        color: getUrgencyInfo(selectedRequest.urgency).color,
                      }}
                    >
                      {getUrgencyInfo(selectedRequest.urgency).label}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Fecha Recepción:</span>
                    <span className={styles.detailValue}>
                      {formatDateTime(selectedRequest.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h3>Información del Cliente</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Cliente:</span>
                    <span className={styles.detailValue}>
                      {selectedRequest.clientName}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Email:</span>
                    <span className={styles.detailValue}>
                      {selectedRequest.clientEmail}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Teléfono:</span>
                    <span className={styles.detailValue}>
                      {selectedRequest.clientPhone}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h3>Servicio Solicitado</h3>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Tipo:</span>
                  <span className={styles.detailValue}>
                    {selectedRequest.type}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Fecha Preferida:</span>
                  <span className={styles.detailValue}>
                    {formatDate(selectedRequest.preferredDate)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Descripción:</span>
                  <p className={styles.detailDescription}>
                    {selectedRequest.description}
                  </p>
                </div>
              </div>

              <div className={styles.modalActions}>
                {selectedRequest.status === "pending" && (
                  <>
                    <button
                      className={`${styles.modalButton} ${styles.review}`}
                      onClick={() => {
                        handleStatusChange(selectedRequest.id, "reviewing");
                      }}
                    >
                      <Icon name="eye" size={18} />
                      Marcar en Revisión
                    </button>
                    <button
                      className={`${styles.modalButton} ${styles.approve}`}
                      onClick={() => {
                        handleApprove(
                          selectedRequest.id,
                          selectedRequest.clientName,
                        );
                        setSelectedRequest(null);
                      }}
                    >
                      <Icon name="document" size={18} />
                      Crear Presupuesto
                    </button>
                  </>
                )}
                {selectedRequest.status === "reviewing" && (
                  <>
                    <button
                      className={`${styles.modalButton} ${styles.approve}`}
                      onClick={() => {
                        handleApprove(
                          selectedRequest.id,
                          selectedRequest.clientName,
                        );
                        setSelectedRequest(null);
                      }}
                    >
                      <Icon name="document" size={18} />
                      Crear Presupuesto
                    </button>
                    <button
                      className={`${styles.modalButton} ${styles.reject}`}
                      onClick={() => {
                        handleStatusChange(selectedRequest.id, "rejected");
                      }}
                    >
                      <Icon name="xCircle" size={18} />
                      Rechazar
                    </button>
                  </>
                )}
                {selectedRequest.status === "approved" && (
                  <button
                    className={`${styles.modalButton} ${styles.schedule}`}
                    onClick={() => {
                      setSelectedRequest(null);
                      window.location.hash = "#/quotes";
                    }}
                  >
                    <Icon name="document" size={18} />
                    Ir a Presupuestos
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestsPage;
